import { useCallback } from 'react'
import { ApolloQueryResult, useApolloClient } from '@apollo/client'
import promotionItemsAvailabilityQuery from './query'
import {
  IPromotionItemsAvailability,
  IPromotionItemsAvailabilityVariables,
} from './types'

/**
 * A function that runs `promotionItemsAvailability` once per call.
 *
 * Built on `client.query` rather than `useLazyQuery` because a caller issues
 * several of these at once, one per chunk of SKUs, and a lazy query's execute
 * function re-drives a single observable, so concurrent calls with different
 * variables are not independent requests.
 *
 * The caller passes variables and nothing else, so the policies below cannot
 * be overridden:
 * - `fetchPolicy: 'no-cache'`: the answer is a stock reading that must be
 *   current each time a product is opened, and the selection is a partial
 *   `Product` that has no business being merged into the entries the catalog
 *   documents cache.
 * - `errorPolicy: 'none'`: a failing resolver rejects the promise with an
 *   `ApolloError`; the caller fails open on any exception, this one
 *   included.
 * @internal
 */
const usePromotionItemsAvailabilityQuery = () => {
  const client = useApolloClient()
  return useCallback(
    (
      variables: IPromotionItemsAvailabilityVariables,
    ): Promise<ApolloQueryResult<IPromotionItemsAvailability>> =>
      client.query<
        IPromotionItemsAvailability,
        IPromotionItemsAvailabilityVariables
      >({
        query: promotionItemsAvailabilityQuery,
        variables,
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
      }),
    [client],
  )
}

export { usePromotionItemsAvailabilityQuery }
